import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { Paginator } from "../../../models/Paginator";
import { JsonPipe, NgForOf, NgIf } from "@angular/common";
import { PaginatorService } from "../../../core/services/paginator/paginator.service";
import { NgxSpinnerService } from "ngx-spinner";
import { SortColumn } from "../../../models/Table/SortColumn";
import { ActiveFilter } from "../../../models/Table/ActiveFilter";
import { LoaderComponent } from "../loader/loader.component";

export interface PaginationOptions {
  alternativePagination?: boolean;
}

@Component({
  selector: 'app-paginator',
  standalone: true,
  imports: [
    JsonPipe,
    NgIf,
    NgForOf,
    LoaderComponent
  ],
  templateUrl: './paginator.component.html',
  styleUrl: './paginator.component.css'
})
export class PaginatorComponent implements OnChanges {
  @Input() paginator!: Paginator<any>;
  @Input() sorts: SortColumn[] = [];
  @Input() pagination: any;

  @Input() filters: ActiveFilter[] = [];
  protected readonly Math = Math;
  @Output() onChangePage = new EventEmitter<Paginator<any>>();
  loading: boolean = false;
  
  @Input() paginationOptions: PaginationOptions = {
    alternativePagination: false
  };
  
  pages: number[] = [];
  @Output() pageChange = new EventEmitter<number>();
  @Output() perPageChange = new EventEmitter<number>();

  constructor(private paginatorService: PaginatorService, private spinner: NgxSpinnerService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paginator'] || changes['paginationOptions']) {
      this.pages = this.getPages();
    }
  }

  loadPage(url: string | undefined) {
    if (url) {
      this.loading = true;
      
      // Comprobamos si debemos usar la paginación alternativa
      if (this.paginationOptions.alternativePagination) {
        // Emitimos el evento de cambio de página para que el componente padre lo maneje
        const pageNumber = this.extractPageNumber(url);
        if (pageNumber) {
          this.pageChange.emit(pageNumber);
        }
        this.loading = false;
      } else {
        // Comportamiento original
        this.paginatorService.paginate(url, this.sorts, this.filters).subscribe({
          next: (data) => {
            if (data.data) {
              this.paginator = data.data;
              this.onChangePage.emit(this.paginator);
            }
            this.loading = false;
          },
          error: (error) => {
            console.log(error);
            this.loading = false;
          },
        });
      }
    }
  }

  private extractPageNumber(url: string): number | null {
    try {
      const urlObj = new URL(url);
      const page = urlObj.searchParams.get('page');
      return page ? parseInt(page, 10) : null;
    } catch (e) {
      console.error("Error parsing URL:", e);
      return null;
    }
  }

  onPerPageSelected(perPage: number) {
    if (this.paginationOptions.alternativePagination) {
      this.perPageChange.emit(perPage);
    }
  }

  getPages(): number[] {
    const totalPagesToShow = 7;
    const pages: number[] = [];
    
    if (this.paginationOptions.alternativePagination) {
      if (this.paginator.last_page <= totalPagesToShow) {
        for (let i = 1; i <= this.paginator.last_page; i++) {
          pages.push(i);
        }
      } else {
        if (this.paginator.current_page <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push(-1); // Elipsis
          pages.push(this.paginator.last_page);
        } else if (this.paginator.current_page >= this.paginator.last_page - 3) {
          pages.push(1);
          pages.push(-1); // Elipsis
          for (let i = this.paginator.last_page - 4; i <= this.paginator.last_page; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push(-1); // Elipsis
          for (let i = this.paginator.current_page - 1; i <= this.paginator.current_page + 1; i++) {
            pages.push(i);
          }
          pages.push(-1); // Elipsis
          pages.push(this.paginator.last_page);
        }
      }
    } else {
      if (this.paginator.last_page <= totalPagesToShow) {
        for (let i = 1; i <= this.paginator.last_page; i++) {
          pages.push(i);
        }
      } else {
        if (this.paginator.current_page <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push(-1); // Elipsis
          pages.push(this.paginator.last_page);
        } else if (this.paginator.current_page >= this.paginator.last_page - 3) {
          pages.push(1);
          pages.push(-1); // Elipsis
          for (let i = this.paginator.last_page - 4; i <= this.paginator.last_page; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push(-1); // Elipsis
          for (let i = this.paginator.current_page - 1; i <= this.paginator.current_page + 1; i++) {
            pages.push(i);
          }
          pages.push(-1); // Elipsis
          pages.push(this.paginator.last_page);
        }
      }
    }
    
    return pages;
  }

  onPageClick(page: number) {
    if (page === -1 || page === this.paginator.current_page) return; 
    
    if (this.paginationOptions.alternativePagination) {
      this.pageChange.emit(page);
    } else {
      let pageUrl = '';
      if (page === 1) {
        pageUrl = this.paginator.first_page_url;
      } else if (page === this.paginator.last_page) {
        pageUrl = this.paginator.last_page_url;
      } else {
        const pageLink = this.paginator.links.find(link => link.label === page.toString());
        if (pageLink) {
          pageUrl = pageLink.url!;
        }
      }
      
      if (pageUrl) {
        this.loadPage(pageUrl);
      }
    }
  }
}